import {Accordion} from "react-bootstrap";
import {FormProvider, useForm} from 'react-hook-form'
import {states} from "../../shared/StatesDropdown";
import FormSelectCustom from "../../shared/components/FormSelectCustom";
import {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {KeyValuePair} from "../../shared/KeyValuePair";
import eventEmitter from "../../shared/eventEmitter";
import {EventEmitterTypes} from "../../shared/eventEmitterEnum";
import {listService} from "../../services/listsService";
import {businessYearService} from "../../services/businessYearService";


const categoryFields = {
    property: [
        "streetNumber", "prefix", "streetName", "suffix", "city",
        "state", "propertyType", "businessName", "postalCode"
    ],
    customer: [
        "firstName", "lastName", "email", "phone", "customerStatusName", 
        "customerStatusId", "customerId", "webId", "link"
    ],
    job: [
        "dateEstimated", "dateDone", "transactionNumber", "transactionDate",
        "depositSlip", "depositDate", "jobStatusName", "jobStatusId"
    ]
} as const;


export type CustomerCategory = keyof typeof categoryFields;
export type CustomerSearchType = Partial<
    Record<"property"|"customer"|"job", Record<string,string>>
>;


export interface CustomerSearchFormHandles {
    clearField(name: string): void;
}

interface CustomerSearchFormProps {
    onSearch: (terms: CustomerSearchType) => void;
    setSearchReady: () => void;
}

const CustomerSearchForm = forwardRef<CustomerSearchFormHandles,CustomerSearchFormProps>
    (function CustomerSearchForm({ onSearch, setSearchReady }, ref) {
        const [customerStatusOptions, setCustomerStatuses] = useState([]);
        const [jobStatusOptions, setJobStatusOptions] = useState([]);

        const methods = useForm<Record<string, string>>();
        const { register, reset, setValue, getValues, handleSubmit } = methods;


        useEffect(() => {
            if(customerStatusOptions && customerStatusOptions.length > 0)
                onSubmit(null);
        }, [customerStatusOptions]);
        const onSubmit = data => {
            const statusId = getValues();
            if(!customerStatusOptions || customerStatusOptions.length === 0) return null;
            if(!jobStatusOptions || jobStatusOptions.length === 0) return null;
            const custStatus = customerStatusOptions.find(x => x.value === Number(statusId.customerStatusId))
            const jobStatus = jobStatusOptions.find(x => x.value === Number(statusId.jobStatusId))
            setValue('customerStatusName', custStatus?.label)
            setValue('customerStatusId', custStatus?.value)
            setValue('jobStatusName', jobStatus?.label)
            setValue('jobStatusId', jobStatus?.value)
            const all = getValues()
            sessionStorage.setItem("customerSearchForm", JSON.stringify(all));
            
            const filled = {} as CustomerSearchType;
            (Object.keys(categoryFields) as CustomerCategory[]).forEach(cat => {
                const keys = categoryFields[cat] as unknown as string[];
                const entries = keys
                    .filter(key => {
                        const v = all[key];
                        return v != null && v !== "" && v !== "Select" && v !== "Unspecified";
                    })
                    .map(key => {
                        let value = all[key];
                        if (typeof value === "string") {
                            value = value.replace(/\*/g, "");
                        }
                        return [key, value] as const;
                    });

                if (entries.length) {
                    filled[cat] = Object.fromEntries(entries);
                }
            });

            onSearch(filled);
        }

        useEffect(() => {
            (async () => {
                const customerStatuses = await listService.getCustomerStatuses();
                const jobStatuses = await listService.getJobStatuses();
                setJobStatusOptions(jobStatuses)
                setCustomerStatuses(customerStatuses)
                const saved = sessionStorage.getItem("customerSearchForm");
                if (saved) {
                    const parsed = JSON.parse(saved) as Record<string, string>;
                    Object.entries(parsed).forEach(([key, value]) => {
                        setValue(key, value);
                    });
                }
                setSearchReady();
                onSubmit(null);
                
            })();
            const refreshListener = () => onSubmit(null);
            eventEmitter.on(EventEmitterTypes.CUSTOMER_SEARCH, refreshListener);

            return () => {
                eventEmitter.off(EventEmitterTypes.CUSTOMER_SEARCH, refreshListener);
            };
        }, []);
        
        function clearAllFields() {
            reset()
            onSearch({} as CustomerSearchType)
            sessionStorage.removeItem("customerSearchForm");
        }

        useImperativeHandle(
            ref,
            () => ({
                clearField(name: string) {
                    const all = getValues()
                    if(name === 'customerStatusName'){
                        setValue('customerStatusId', null)
                    }
                    if(name === 'jobStatusName'){
                        setValue('jobStatusId', null)
                    }
                    setValue(name as any, null);
                    sessionStorage.setItem("customerSearchForm", JSON.stringify(getValues()));
                },
            }),
            [setValue]
        );
        return (
            <FormProvider {...methods}>
                <form style={{display: 'contents'}} onSubmit={handleSubmit(onSubmit)}>
                    <div className="col-lg-3 g-0 bg-white filterCol">
                        <Accordion defaultActiveKey={['0', '1', '2']} className='searchAccordions' id='searchAccordions'
                                   alwaysOpen flush>
                            {/*<!-- PROPERTY FILTERS -->*/}
                            <Accordion.Item eventKey='0'>
                                <Accordion.Header>
                                    <span className="me-2"><i className="fa-solid fa-house"></i></span>Property
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="row g-2">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">StNum</label>
                                            <input {...register("streetNumber")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Prefix</label>
                                            <input {...register("prefix")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-8 mb-3">
                                            <label htmlFor="textInput" className="form-label">St Name</label>
                                            <input {...register("streetName")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-4 mb-3">
                                            <label htmlFor="textInput" className="form-label">Suffix</label>
                                            <input {...register("suffix")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-md-12 mb-3">
                                            <label htmlFor="textInput" className="form-label">City</label>
                                            <input {...register("city")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Zip</label>
                                            <input {...register("postalCode")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <FormSelectCustom
                                                label="State"
                                                name="state"
                                                register={register}
                                                options={states}
                                            />
                                        </div>
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-12 mb-3">
                                            <label className="form-label">Property Type</label>
                                            <select {...register("propertyType")} className="form-select form-select-sm"
                                                    aria-label="select input">
                                                <option value="Unspecified" selected>Select</option>
                                                <option value="Residential">Residential</option>
                                                <option value="Commercial">Commercial</option>
                                            </select>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label htmlFor="textInput" className="form-label">Business Name</label>
                                            <input {...register("businessName")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                            {/*<!-- CUSTOMER FILTERS -->*/}
                            <Accordion.Item eventKey='1'>
                                <Accordion.Header>
                                    <span className="me-2"><i className="fa-solid fa-user"></i></span>Customer
                                </Accordion.Header>
                                <Accordion.Body id="accCustomer">
                                    <div className="row g-2">
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">First Name</label>
                                            <input {...register("firstName")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Last Name</label>
                                            <input {...register("lastName")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Email</label>
                                            <input {...register("email")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Phone</label>
                                            <input {...register("phone")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                    </div>
                                    <div className="row g-2">
                                        <div className="col-12 mb-3">
                                            <FormSelectCustom
                                                label="Customer Status"
                                                name="customerStatusId"
                                                register={register}
                                                options={customerStatusOptions}
                                            />
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label htmlFor="textInput" className="form-label">Customer ID</label>
                                            <input {...register("customerId")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label htmlFor="textInput" className="form-label">Web ID</label>
                                            <input {...register("webId")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-12 mb-3">
                                            <label htmlFor="textInput" className="form-label">Link</label>
                                            <input {...register("link")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                            {/*<!-- JOB FILTERS -->*/}
                            <Accordion.Item eventKey='2'>
                                <Accordion.Header>
                                    <span className="me-2"><i className="fa-solid fa-file-invoice"></i></span>Job
                                </Accordion.Header>
                                <Accordion.Body>
                                    <div className="row g-2">
                                        <div className="col-12 mb-3">
                                            <FormSelectCustom
                                                label="Job Status"
                                                name="jobStatusId"
                                                register={register}
                                                options={jobStatusOptions}
                                            />
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Date Estimated</label>
                                            <input {...register("dateEstimated")} type="date"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Date Done</label>
                                            <input {...register("dateDone")} type="date"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Trans Number</label>
                                            <input {...register("transactionNumber")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Trans Date</label>
                                            <input {...register("transactionDate")} type="date"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Deposit Slip</label>
                                            <input {...register("depositSlip")} type="text"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                        <div className="col-md-6 mb-3">
                                            <label htmlFor="textInput" className="form-label">Deposit Date</label>
                                            <input {...register("depositDate")} type="date"
                                                   className="form-control form-control-sm"
                                                   id="textInput" aria-describedby="textInput" placeholder="text"/>
                                        </div>
                                    </div>
                                </Accordion.Body>
                            </Accordion.Item>
                        </Accordion>
                        <div className="position-sticky bottom-0 bg-white p-4 w-100 shadow-md">
                            <div className="d-flex flex-column gap-2">
                                <button type="submit" className="btn btn-primary w-100 py-2">
                                    Search
                                </button>
                                <button type="button" onClick={clearAllFields} className="btn btn-cancel w-100 py-2">
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </FormProvider>
        );
    })

export default CustomerSearchForm;